<script setup lang="ts">
import draggable from 'vuedraggable'
import { computed, ref } from 'vue'

type FieldStatus = 'required' | 'optional' | 'disabled'

type PocField = {
  id: string
  type: 'field'
  title: string
  status: FieldStatus
}

type PocSection = {
  id: string
  type: 'section'
  title: string
  items: PocField[]
}

type BuilderBlock = PocField | PocSection

type DragMoveEvent = {
  draggedContext?: {
    element?: BuilderBlock
  }
}

const fieldGroup = { name: 'poc-fields' }

const statusOptions = [
  { title: 'Obrigatório', value: 'required' },
  { title: 'Não obrigatório', value: 'optional' },
  { title: 'Desabilitado', value: 'disabled' },
]

const blocks = ref<BuilderBlock[]>([
  { id: 'field-1', type: 'field', title: 'Nome do campo', status: 'optional' },
  {
    id: 'section-1',
    type: 'section',
    title: 'Nome da seção',
    items: [
      { id: 'field-2', type: 'field', title: 'E-mail', status: 'required' },
    ],
  },
  {
    id: 'field-3',
    type: 'field',
    title: 'Campo solto entre seções',
    status: 'disabled',
  },
  {
    id: 'section-2',
    type: 'section',
    title: 'Outra seção',
    items: [],
  },
])

const totalFields = computed(() =>
  blocks.value.reduce((acc, block) => {
    if (block.type === 'field') return acc + 1
    return acc + block.items.length
  }, 0)
)

function addField() {
  blocks.value.push({
    id: `field-${Date.now()}`,
    type: 'field',
    title: 'Novo campo',
    status: 'optional',
  })
}

function addSection() {
  blocks.value.push({
    id: `section-${Date.now()}`,
    type: 'section',
    title: 'Nova seção',
    items: [],
  })
}

function removeRootField(fieldId: string) {
  blocks.value = blocks.value.filter((block) => block.id !== fieldId)
}

function removeSection(sectionId: string) {
  const index = blocks.value.findIndex((block) => block.id === sectionId)
  if (index === -1) return

  const section = blocks.value[index]
  if (section.type !== 'section') return

  blocks.value.splice(index, 1, ...section.items)
}

function removeSectionField(sectionId: string, fieldId: string) {
  const section = blocks.value.find(
    (block): block is PocSection =>
      block.type === 'section' && block.id === sectionId
  )

  if (!section) return

  section.items = section.items.filter((item) => item.id !== fieldId)
}

function canMoveIntoSection(event: DragMoveEvent) {
  return event.draggedContext?.element?.type === 'field'
}
</script>

<template>
  <v-card class="form-builder-card" flat>
    <div class="form-builder-header">
      <p class="text-h6 ma-0">Campos do formulário</p>

      <div class="d-flex align-center ga-3">
        <v-btn
          variant="text"
          color="grey-darken-2"
          prepend-icon="mdi-card-text-outline"
        >
          Adicionar nota
        </v-btn>

        <v-btn
          variant="text"
          color="grey-darken-2"
          prepend-icon="mdi-view-sequential-outline"
          @click="addSection"
        >
          Adicionar seção
        </v-btn>

        <v-btn
          color="light-green-darken-1"
          prepend-icon="mdi-plus"
          @click="addField"
        >
          Adicionar campo
        </v-btn>
      </div>
    </div>

    <div class="form-builder-body">
      <draggable
        v-model="blocks"
        :group="fieldGroup"
        item-key="id"
        handle=".root-drag-handle"
        ghost-class="ghost"
        animation="180"
      >
        <template #item="{ element }">
          <div>
            <div v-if="element.type === 'field'" class="field-row">
              <v-icon class="root-drag-handle" size="20">
                mdi-drag-vertical
              </v-icon>

              <p class="field-title ma-0">
                {{ element.title }}
              </p>

              <v-select
                v-model="element.status"
                :items="statusOptions"
                density="compact"
                variant="outlined"
                hide-details
                class="field-status"
              />

              <v-btn
                icon="mdi-delete-outline"
                variant="text"
                color="red"
                size="small"
                @click="removeRootField(element.id)"
              />
            </div>

            <div v-else class="section-card">
              <div class="section-header">
                <div class="d-flex align-center flex-grow-1">
                  <v-icon class="root-drag-handle mr-2" size="20">
                    mdi-drag-vertical
                  </v-icon>

                  <v-text-field
                    v-model="element.title"
                    density="compact"
                    variant="plain"
                    hide-details
                    class="section-title-input"
                  />
                </div>

                <div class="d-flex align-center ga-1">
                  <v-btn
                    icon="mdi-pencil"
                    variant="text"
                    color="grey-darken-1"
                    size="small"
                  />

                  <v-btn
                    icon="mdi-delete-outline"
                    variant="text"
                    color="red"
                    size="small"
                    @click="removeSection(element.id)"
                  />
                </div>
              </div>

              <draggable
                v-model="element.items"
                :group="fieldGroup"
                item-key="id"
                handle=".field-drag-handle"
                ghost-class="ghost"
                animation="180"
                class="section-dropzone"
                :move="canMoveIntoSection"
              >
                <template #item="{ element: field }">
                  <div class="field-row section-field-row">
                    <v-icon class="field-drag-handle" size="20">
                      mdi-drag-vertical
                    </v-icon>

                    <p class="field-title ma-0">
                      {{ field.title }}
                    </p>

                    <v-select
                      v-model="field.status"
                      :items="statusOptions"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="field-status"
                    />

                    <v-btn
                      icon="mdi-delete-outline"
                      variant="text"
                      color="red"
                      size="small"
                      @click="removeSectionField(element.id, field.id)"
                    />
                  </div>
                </template>

                <template #footer>
                  <div
                    v-if="!element.items.length"
                    class="section-empty-message"
                  >
                    Arraste e solte os campos para dentro desta seção
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </template>
      </draggable>

      <div class="text-caption text-grey mt-4">
        Total de campos: {{ totalFields }}
      </div>
    </div>
  </v-card>
</template>

<style scoped>
.form-builder-card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #fff;
}

.form-builder-header {
  min-height: 72px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.form-builder-body {
  padding: 16px 24px 32px;
}

.field-row {
  min-height: 48px;
  display: grid;
  grid-template-columns: 28px 1fr 220px 40px;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  background: #fff;
}

.field-row:hover {
  background: #fafafa;
}

.root-drag-handle,
.field-drag-handle {
  cursor: grab;
  color: #757575;
}

.root-drag-handle:active,
.field-drag-handle:active {
  cursor: grabbing;
}

.field-title {
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.field-status {
  max-width: 220px;
}

.section-card {
  margin-top: 8px;
  padding: 12px 16px 0;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #fff;
}

.section-header {
  min-height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.section-title-input {
  max-width: 420px;
}

.section-title-input :deep(input) {
  font-size: 14px;
  font-weight: 600;
}

.section-dropzone {
  min-height: 34px;
  border-bottom: 2px solid #7ac143;
}

.section-empty-message {
  padding: 6px 0 8px 28px;
  color: #757575;
  font-size: 12px;
}

.ghost {
  opacity: 0.45;
  background: #f1f8e9;
}

@media (max-width: 960px) {
  .form-builder-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .form-builder-header > div {
    width: 100%;
    flex-wrap: wrap;
  }

  .field-row {
    grid-template-columns: 28px 1fr 40px;
  }

  .field-status {
    grid-column: 2 / 4;
    max-width: 100%;
  }
}
</style>
